/**
 * Fuente de verdad del contenido comercial de la landing.
 *
 * REGLA: no se inventan datos del producto. Todo dato que todavía no fue
 * provisto queda como PLACEHOLDER entre corchetes para completarlo después.
 */

export interface Tomo {
  num: string
  name: string
  /** Cantidad de recetas del tomo (placeholder si no fue provista). */
  count: string
  desc: string
}
export interface RecipeAttr {
  title: string
  desc: string
  /** Marca el atributo destacado (Keto + Sin TACC a la vez). */
  highlight?: boolean
}
export interface InteriorFeature {
  title: string
  desc: string
}
export interface AudienceItem {
  title: string
  desc: string
}
export interface Bono {
  num: string
  name: string
  /** Para qué sirve dentro de la colección. */
  role: string
  desc: string
  cover?: string
}
export interface Faq {
  q: string
  a: string
}

/** Nombre comercial único del producto. */
export const PRODUCT_NAME = '500+ Recetas Keto + Sin TACC'

/**
 * Precio definitivo. Formato monetario único de la landing: siempre "$00.000".
 */
export const PRICE = {
  previous: '$29.990',
  current: '$12.990',
  note: 'Precio en pesos argentinos · Pago único',
} as const

/** PLACEHOLDER — completar con la política de garantía real (si existe). */
export const GUARANTEE_PLACEHOLDER = '[COMPLETAR: garantía / política de devolución]'

/** PLACEHOLDER — completar con los medios de pago habilitados. */
export const PAYMENT_PLACEHOLDER = '[COMPLETAR: medios de pago]'

export const PRODUCT_FACTS: string[] = [
  '+500 recetas',
  '6 tomos digitales',
  'Keto + Sin TACC',
  '+4 bonos',
]

/** Hoy → Con la colección (única sección de problema/transformación). */
export const TRANSFORM_BEFORE: string[] = [
  'Abrís la heladera y no sabés qué cocinar.',
  'Comés siempre los mismos tres o cuatro platos.',
  'Las recetas que encontrás son keto, pero llevan harina.',
  'Piden ingredientes que acá no se consiguen.',
  'Te aburrís y terminás saliéndote de tu alimentación.',
]

export const TRANSFORM_AFTER: string[] = [
  'Abrís la colección y elegís entre más de 500 recetas.',
  'Tenés variedad para cada momento del día.',
  'Cada receta es Keto y Sin TACC al mismo tiempo.',
  'Ingredientes que conseguís en cualquier supermercado.',
  'Comés rico todos los días y sostenés tu alimentación.',
]

/** Los 6 tomos de la colección (nombres tal como figuran en las portadas). */
export const TOMOS: Tomo[] = [
  {
    num: '01',
    name: 'Desayunos y Meriendas',
    count: '85 recetas',
    desc: 'Para arrancar el día y para la tarde, sin harinas ni azúcar.',
  },
  {
    num: '02',
    name: 'Ensaladas y Platos Frescos',
    count: '[CANTIDAD] recetas',
    desc: 'Frescas, rápidas y fáciles de llevar al trabajo.',
  },
  {
    num: '03',
    name: 'Cenas',
    count: '85 recetas',
    desc: 'Platos completos para resolver la noche sin repetir siempre lo mismo.',
  },
  {
    num: '04',
    name: 'Carnes y Aves',
    count: '[CANTIDAD] recetas',
    desc: 'Cortes, pollo y preparaciones al horno, a la plancha y a la sartén.',
  },
  {
    num: '05',
    name: 'Pescados y Mariscos',
    count: '[CANTIDAD] recetas',
    desc: 'Opciones livianas, nutritivas y listas en minutos.',
  },
  {
    num: '06',
    name: 'Sopas, Cremas, Guarniciones y Extras',
    count: '[CANTIDAD] recetas',
    desc: 'Todo lo que acompaña: sopas, cremas, guarniciones y básicos.',
  },
]

/** Atributos que cumple cada receta (tomados de la tapa de la colección). */
export const RECIPE_ATTRS: RecipeAttr[] = [
  {
    title: 'Keto + Sin TACC a la vez',
    desc: 'No tenés que elegir entre una cosa y la otra: cada receta cumple con las dos.',
    highlight: true,
  },
  { title: 'Bajas en carbos', desc: 'Pensadas para no sacarte de tu alimentación.' },
  { title: 'Altas en grasas saludables', desc: 'Palta, aceite de oliva, frutos secos, huevo.' },
  { title: 'Nutritivas', desc: 'Platos completos, no soluciones de emergencia.' },
  { title: 'Listas en minutos', desc: 'Preparaciones simples para el día a día.' },
  { title: 'Ingredientes accesibles', desc: 'Se consiguen en Argentina, en el súper de siempre.' },
]

/** Cómo se ve una receta por dentro. */
export const INTERIOR_FEATURES: InteriorFeature[] = [
  { title: 'Foto del plato', desc: 'Ves cómo queda antes de cocinarlo.' },
  { title: 'Ingredientes claros', desc: 'Lista corta, con cantidades y nombres locales.' },
  { title: 'Tiempo y porciones', desc: 'Sabés cuánto tarda y para cuántos rinde.' },
  { title: 'Paso a paso', desc: 'Instrucciones numeradas, en simple, sin tecnicismos.' },
]

/** Para quién es la colección. */
export const AUDIENCE: AudienceItem[] = [
  {
    title: 'Hacés Keto y te aburriste',
    desc: 'Necesitás variedad real para no comer siempre lo mismo.',
  },
  {
    title: 'Tenés que comer Sin TACC',
    desc: 'Buscás recetas seguras, sin harinas de trigo, avena ni cebada.',
  },
  {
    title: 'Cocinás para toda la familia',
    desc: 'Querés platos que le gusten a todos, no un menú aparte.',
  },
  {
    title: 'Tenés poco tiempo',
    desc: 'Preferís preparaciones simples y rápidas de resolver.',
  },
  {
    title: 'Recién empezás',
    desc: 'Querés algo ordenado que te diga qué cocinar en cada momento del día.',
  },
]

/** Los 4 bonos de regalo (nombres tal como figuran en el material del producto). */
export const BONOS: Bono[] = [
  {
    num: '01',
    name: 'Lista de Súper Alimentos Keto',
    role: 'Para saber qué comprar',
    desc: 'Los alimentos que conviene tener siempre en casa para cocinar sin pensarlo.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/290769a2-eaca-4c26-a353-7d2ed0ddec7d.jpg',
  },
  {
    num: '02',
    name: 'Plan Semanal de Comidas',
    role: 'Para no improvisar',
    desc: 'Tu semana organizada: qué cocinar cada día, sin decidir a último momento.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/c58752d6-d7de-4537-904b-24dfe0304af9.jpg',
  },
  {
    num: '03',
    name: 'Tablas de Equivalencias y Conversiones',
    role: 'Para medir sin balanza',
    desc: 'Tazas, cucharadas y gramos: convertís cualquier cantidad en segundos.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/f6ff44c4-839f-4598-bfc7-29c9dc699690.jpg',
  },
  {
    num: '04',
    name: 'Snacks Express',
    role: 'Para el hambre entre comidas',
    desc: 'Opciones rápidas para cuando querés picar algo y no salirte del plan.',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/eb982e33-7dd1-4ac6-8aaf-ccbb2168ace3.jpg',
  },
]

/** Contenido de la colección, dentro de la sección de oferta. */
export const OFFER_INCLUDES: string[] = [
  '6 tomos digitales con más de 500 recetas',
  'Todas las recetas Keto y Sin TACC al mismo tiempo',
  'Ingredientes que se consiguen en Argentina',
  'Desayunos, meriendas, ensaladas, cenas, carnes, pescados y extras',
  'Cada receta con foto, ingredientes, tiempo, porciones y paso a paso',
  'Formato visual para leer desde el celular, la tablet o la compu',
]

export const FAQS: Faq[] = [
  {
    q: '¿Las recetas son Keto y Sin TACC al mismo tiempo?',
    a: 'Sí. Cada receta de los 6 tomos está pensada para cumplir con las dos cosas a la vez: baja en carbohidratos y sin harinas con gluten.',
  },
  {
    q: '¿Necesito ingredientes difíciles de conseguir?',
    a: 'No. La colección está adaptada al público argentino: los ingredientes se consiguen en supermercados y dietéticas comunes.',
  },
  {
    q: '¿Sirve si recién empiezo con Keto?',
    a: 'Sí. Las recetas están explicadas paso a paso, en simple, y están ordenadas por momento del día para que sepas qué cocinar en cada comida.',
  },
  {
    q: '¿Son recetas complicadas?',
    a: 'No. Son preparaciones simples, con pocos pasos y tiempos cortos, pensadas para una cocina hogareña.',
  },
  {
    q: '¿Cómo recibo la colección?',
    a: 'En formato digital, inmediatamente después de la compra. No se envía nada físico.',
  },
  {
    q: '¿Puedo verla desde el celular?',
    a: 'Sí. La abrís desde el celular, la tablet o la computadora, y la tenés siempre a mano en la cocina.',
  },
  {
    q: '¿Es un pago único? ¿Qué medios de pago hay?',
    a: `Sí, es una compra única de ${PRICE.current}, sin suscripciones ni pagos mensuales. Medios de pago: ${PAYMENT_PLACEHOLDER}.`,
  },
  {
    q: '¿Los 4 bonos están incluidos?',
    a: 'Sí. Los 4 bonos de regalo forman parte de la compra, sin costo adicional.',
  },
  {
    q: '¿Tiene garantía?',
    a: GUARANTEE_PLACEHOLDER,
  },
]
